import { useEffect, useState } from 'react';
import { invalidateCachedValue } from '../lib/requestCache';
import { toast } from 'react-toastify';
import { useI18n } from '../i18n';
import { getStatisticsRefreshStatusApi, STATISTICS_REFRESH_EVENT } from '../lib/matchesApi';

export function StatisticsRefreshNotifications() {
  const { t, locale } = useI18n();
  useEffect(() => {
    const notify = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      const date = new Date(detail?.run_at);
      if (!Number.isFinite(date.getTime())) return;
      toast.info(t('statistics_refresh_planned', { time: date.toLocaleString(locale === 'nl' ? 'nl-NL' : 'en-GB', {
        day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      }) }), { toastId: `statistics-refresh-${detail.task_id}`, autoClose: 15000, closeButton: true, closeOnClick: false, draggable: false, pauseOnHover: false, pauseOnFocusLoss: false });
    };
    const scheduleRemoved = (event: Event) => toast.info(t('schedule_preparation_removed'), {
      toastId: `schedule-removed-${(event as CustomEvent).detail?.matchId}`, autoClose: 12000,
    });
    window.addEventListener('schedule-invalidated', scheduleRemoved);
    window.addEventListener(STATISTICS_REFRESH_EVENT, notify);
    return () => {
      window.removeEventListener(STATISTICS_REFRESH_EVENT, notify);
      window.removeEventListener('schedule-invalidated', scheduleRemoved);
    };
  }, [t, locale]);
  return null;
}

interface RefreshStatus {
  task_id: string;
  status: string;
  run_at: string;
  player_ids: string[];
}

export default function StatisticsRefreshNotice({ clubId, playerIds, onRefresh }: {
  clubId?: string; playerIds: string[]; onRefresh: () => Promise<void>;
}) {
  const { t, locale } = useI18n();
  const [rows, setRows] = useState<RefreshStatus[]>([]);
  const [unavailable, setUnavailable] = useState(false);
  const ids = new Set(playerIds);
  const pending = rows.filter(row => row.player_ids.some(id => ids.has(id)));
  useEffect(() => {
    let active = true;
    let busy = false;
    let previousTaskIds = new Set<string>();
    let initialized = false;
    setRows([]);
    setUnavailable(false);
    if (!clubId) return;
    const check = async () => {
      if (busy) return;
      busy = true;
      try {
        const rows = await getStatisticsRefreshStatusApi(clubId) as RefreshStatus[];
        if (!active) return;
        const nextTaskIds = new Set(rows.map(row => row.task_id));
        if (!initialized || [...previousTaskIds].some(id => !nextTaskIds.has(id))) {
          invalidateCachedValue('players:');
          await onRefresh();
          initialized = true;
        }
        if (!active) return;
        previousTaskIds = nextTaskIds;
        setRows(rows);
        setUnavailable(false);
      } catch {
        if (active) setUnavailable(true);
      } finally { busy = false; }
    };
    void check();
    const refresh = () => { void check(); };
    const interval = window.setInterval(refresh, 15000);
    window.addEventListener('focus', refresh);
    window.addEventListener(STATISTICS_REFRESH_EVENT, refresh);
    return () => {
      active = false;
      window.clearInterval(interval);
      window.removeEventListener('focus', refresh);
      window.removeEventListener(STATISTICS_REFRESH_EVENT, refresh);
    };
  }, [clubId, onRefresh]);
  if (!pending.length && !unavailable) return null;
  const failed = pending.some(row => ['failed', 'interrupted', 'cancelled'].includes(row.status));
  const processing = pending.some(row => row.status === 'processing');
  const earliest = pending[0]?.run_at;
  const date = earliest ? new Date(earliest) : null;
  const time = date?.toLocaleString(locale === 'nl' ? 'nl-NL' : 'en-GB', {
    day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
  }) || '';
  const overdue = date && date.getTime() < Date.now();
  const key = unavailable ? (pending.length ? 'statistics_refresh_outdated_unavailable' : 'statistics_refresh_unavailable') : failed ? 'statistics_refresh_failed'
    : processing ? 'statistics_refresh_processing' : overdue ? 'statistics_refresh_waiting' : 'statistics_refresh_outdated';
  return <div role="status" className="mb-4 border-y border-primary-100 bg-primary-50 px-4 py-3 text-sm text-gray-800">{t(key, { time })}</div>;
}
